import { Injectable } from '@angular/core';
import { Owner } from '../models/owner.model';
import { OwnerService } from './owner.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  private currentOwnerSubject = new BehaviorSubject<Owner | null>(null);
  public currentOwner$ = this.currentOwnerSubject.asObservable();

  constructor(private ownerService: OwnerService) {}

  /**
   * Apply owner branding to the application
   * @param owner The owner object containing branding information
   */
  applyOwnerBranding(owner: Owner): void {
    if (!owner) return;

    const root = document.documentElement;

    // Apply owner-specific colors for station badges and owner-specific elements
    // These won't override the main RAD branding
    if (owner.theme_color_primary) {
      root.style.setProperty('--owner-primary-color', owner.theme_color_primary);
      root.style.setProperty('--owner-primary-light', this.lightenColor(owner.theme_color_primary, 20));
      root.style.setProperty('--owner-primary-dark', this.darkenColor(owner.theme_color_primary, 20));
    }

    if (owner.theme_color_secondary) {
      root.style.setProperty('--owner-secondary-color', owner.theme_color_secondary);
      root.style.setProperty('--owner-secondary-light', this.lightenColor(owner.theme_color_secondary, 20));
      root.style.setProperty('--owner-secondary-dark', this.darkenColor(owner.theme_color_secondary, 20));
    }

    if (owner.theme_color_accent) {
      root.style.setProperty('--owner-accent-color', owner.theme_color_accent);
      root.style.setProperty('--owner-accent-light', this.lightenColor(owner.theme_color_accent, 20));
      root.style.setProperty('--owner-accent-dark', this.darkenColor(owner.theme_color_accent, 20));
    }

    if (owner.theme_color_neutral) {
      root.style.setProperty('--owner-text-secondary', owner.theme_color_neutral);
      root.style.setProperty('--owner-text-muted', this.lightenColor(owner.theme_color_neutral, 30));
    }

    // Store current owner for reference
    this.currentOwnerSubject.next(owner);
  }

  /**
   * Load and apply branding for a user based on their ownerid
   * @param ownerid The owner ID to load branding for
   */
  loadUserBranding(ownerid: number): Observable<Owner | null> {
    return new Observable(observer => {
      this.ownerService.getOwner(ownerid).subscribe({
        next: (owner) => {
          this.applyOwnerBranding(owner);
          observer.next(owner);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading owner branding:', error);
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /**
   * Reset branding to default values
   */
  resetToDefaultBranding(): void {
    const root = document.documentElement;
    
    // Reset to Radio Ads Direct brand colors
    root.style.setProperty('--primary-color', '#86C440');
    root.style.setProperty('--primary-light', '#9dd55a');
    root.style.setProperty('--primary-dark', '#6ba335');
    
    root.style.setProperty('--secondary-color', '#666665');
    root.style.setProperty('--secondary-light', '#8a8a89');
    root.style.setProperty('--secondary-dark', '#4d4d4c');
    
    root.style.setProperty('--accent-color', '#86C440');
    root.style.setProperty('--accent-light', '#9dd55a');
    root.style.setProperty('--accent-dark', '#6ba335');
    
    root.style.setProperty('--text-secondary', '#666665');
    root.style.setProperty('--text-muted', '#8a8a89');

    // Clear owner-specific branding variables
    root.style.removeProperty('--owner-primary-color');
    root.style.removeProperty('--owner-primary-light');
    root.style.removeProperty('--owner-primary-dark');
    root.style.removeProperty('--owner-secondary-color');
    root.style.removeProperty('--owner-secondary-light');
    root.style.removeProperty('--owner-secondary-dark');
    root.style.removeProperty('--owner-accent-color');
    root.style.removeProperty('--owner-accent-light');
    root.style.removeProperty('--owner-accent-dark');
    root.style.removeProperty('--owner-text-secondary');
    root.style.removeProperty('--owner-text-muted');

    this.currentOwnerSubject.next(null);
  }

  /**
   * Get the current owner's logo URL
   */
  getCurrentLogo(): string | null {
    const currentOwner = this.currentOwnerSubject.value;
    return currentOwner?.logo || null;
  }

  /**
   * Get the current owner's inverted logo URL
   */
  getCurrentInvertedLogo(): string | null {
    const currentOwner = this.currentOwnerSubject.value;
    return currentOwner?.inverted_logo || null;
  }

  /**
   * Lighten a color by a percentage
   */
  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  /**
   * Darken a color by a percentage
   */
  private darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
      (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
      (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
  }
}
